import { useCallback, useContext } from 'react';

import PortalContext from '../context/portal/context';
import { PageKey } from '../enums/pageKey';
import { Portal } from '../enums/portal.enum';
import {
  dllProperties,
  FormContents,
  lessonPlanProperties,
  unitPlanProperties,
  quizMakerSingleProperties,
} from '../types/page';
import { EVENTS, trackEvent } from '../utils/tracking';

interface UseGenerationTrackingProps {
  pageKey: PageKey;
  formContents: FormContents;
  isPremium?: boolean;
}

const PAGE_TO_EVENT_MAP = {
  [PageKey.lessonPlan]: (isPremium) =>
    isPremium ? EVENTS.GENERATE_PREMIUM_LP : EVENTS.GENERATE_LP,
  [PageKey.objective]: () => EVENTS.GENERATE_OBJECTIVE,
  [PageKey.quizMakerSingle]: () => EVENTS.GENERATE_QUIZ_MAKER,
  [PageKey.activities]: () => EVENTS.GENERATE_ACTIVITY,
  [PageKey.analogyContextualizer]: () => EVENTS.GENERATE_ANALOGY,
  [PageKey.subjectExtention]: () => EVENTS.GENERATE_SUBJECT_EXTENSIONS,
  [PageKey.dailyLessonLog]: () => EVENTS.GENERATE_DAILY_LESSON_LOG,
  [PageKey.catchUpFridays]: () => EVENTS.GENERATE_CATCH_UP_FRIDAYS,
  [PageKey.summativeTest]: () => EVENTS.GENERATE_SUMMATIVE_TEST,
  [PageKey.cerebroLessonPlan]: () => EVENTS.GENERATE_CEREBRO_LESSON_PLAN,
  [PageKey.topicExplorer]: () => EVENTS.GENERATE_TOPIC_EXPLORER,
  [PageKey.energizer]: () => EVENTS.GENERATE_ENERGIZER,
  [PageKey.elif]: () => EVENTS.GENERATE_ELI5,
  [PageKey.unitPlan]: () => EVENTS.GENERATE_UNIT_PLAN,
  [PageKey.photoBooth]: () => EVENTS.GENERATE_PHOTO,
  [PageKey.pptMaker]: () => EVENTS.GENERATE_PPT,
  [PageKey.genericUnpacking]: () => EVENTS.GENERATE_GENERIC_UNPACKING,
  [PageKey.iplanObjective]: () => EVENTS.GENERATE_IPLAN_OBJECTIVE,
  [PageKey.multigradeLessonPlan]: () => EVENTS.GENERATE_MULTIGRADE_LP,
} as const;

const PAGE_TO_FORMAT_MAP = {
  [PageKey.lessonPlan]: (
    formContents: FormContents,
    selectedPortal: Portal,
  ) => {
    if (selectedPortal === Portal.DEFAULT) return null;

    const format =
      (formContents as lessonPlanProperties).lpGradeLevel === 'College'
        ? 'lpCollege'
        : (formContents as lessonPlanProperties).lpLessonFormat;
    return format;
  },
  [PageKey.dailyLessonLog]: (formContents: FormContents) => {
    const hasNonEmptyObjectives = (dayObjectives: string[] | null): boolean => {
      return dayObjectives
        ? dayObjectives.some((objective) => objective.trim() !== '')
        : false;
    };

    const numberOfDays = (formContents as dllProperties).dllObjectives.filter(
      (obj) => hasNonEmptyObjectives(obj.dayObjectives),
    ).length;

    return `${numberOfDays} days`;
  },
  [PageKey.unitPlan]: (formContents: FormContents) => {
    const format = (formContents as unitPlanProperties).format;
    return format;
  },
  [PageKey.quizMakerSingle]: (
    formContents: FormContents,
    selectedPortal: Portal,
  ) => {
    if (selectedPortal === Portal.DEFAULT) return null;
    const gradeLevel = (formContents as quizMakerSingleProperties)
      .quizGradeLevel;
    return gradeLevel === 'College' ? 'College' : '';
  },
  [PageKey.activities]: (
    formContents: FormContents,
    selectedPortal: Portal,
  ) => {
    if (selectedPortal === Portal.DEFAULT) return null;
  },
} as const; //unique results features are handled in their own pages. (ppt maker and photobooth)

export function useGenerationTracking({
  pageKey,
  formContents,
  isPremium = false,
}: UseGenerationTrackingProps) {
  const { selectedPortal } = useContext(PortalContext);

  const trackGenerationEvent = useCallback(
    (isSuccess: boolean) => {
      const baseEvent =
        PAGE_TO_EVENT_MAP[pageKey]?.(isPremium) || EVENTS.GENERATE_LP;
      const format =
        PAGE_TO_FORMAT_MAP[pageKey]?.(formContents, selectedPortal) || null;

      const eventProperties = {
        curriculum: selectedPortal,
        status: isSuccess ? 'Success' : 'Attempt',
        isPremium,
        pageKey,
      };

      trackEvent(
        baseEvent,
        { formContents },
        {
          ...eventProperties,
          format,
          ...(pageKey === PageKey.summativeTest && {
            topics: (formContents as any).topics?.filter(Boolean),
          }),
        },
      );
    },
    [formContents, pageKey, isPremium, selectedPortal],
  );

  return {
    trackGenerationAttempt: () => trackGenerationEvent(false),
    trackGenerationSuccess: () => trackGenerationEvent(true),
  };
}
